import api from '../utils/api';
import { toast } from 'react-toastify';

/**
 * Service for admin-specific operations
 * All functions require admin privileges
 * 
 * Note: This service handles possible API path issues by implementing
 * multiple path fallbacks in case the first attempt fails with 404.
 */
const adminService = {
  /**
   * Get all users
   * @param {Object} params - Query parameters (limit, offset)
   * @returns {Promise} - API response with users
   */
  getAllUsers: async (params = {}) => {
    try {
      const response = await api.get('/admin/users', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch users' };
    }
  },

  /**
   * Get user by ID
   * @param {string} id - User ID
   * @returns {Promise} - API response with user details
   */
  getUserById: async (id) => {
    try {
      const response = await api.get(`/admin/users/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch user details' };
    }
  },

  /**
   * Get certificates for specific user
   * @param {string} userId - User ID
   * @returns {Promise} - API response with user certificates
   */
  getUserCertificates: async (userId) => {
    try {
      console.log(`[AdminService] Fetching certificates for user ${userId}`);
      // First try the admin-specific endpoint
      try {
        const response = await api.get(`/admin/users/${userId}/certificates`);
        console.log(`[AdminService] Successfully fetched ${response.data?.certificates?.length || 0} certificates`);
        return response.data;
      } catch (adminError) {
        console.log(`[AdminService] Admin route failed with:`, adminError);
        
        // If admin route fails, fall back to the general certificates endpoint
        console.log(`[AdminService] Trying alternate certificates endpoint`);
        try {
          const response = await api.get(`/certificates/user/${userId}`);
          console.log(`[AdminService] Alternative endpoint success: ${response.data?.certificates?.length || 0} certificates`);
          return response.data;
        } catch (endpointError) {
          // Last attempt - try direct user endpoint
          try {
            const response = await api.get(`/users/${userId}/certificates`);
            console.log(`[AdminService] Final endpoint success: ${response.data?.certificates?.length || 0} certificates`);
            return response.data;
          } catch (finalError) {
            console.error('[AdminService] All certificate endpoints failed:', finalError);
            throw finalError;
          }
        }
      }
    } catch (error) {
      console.error('[AdminService] Certificate fetch completely failed:', error);
      return { certificates: [], message: 'Could not fetch certificates' };
    }
  },

  /**
   * Update user status (active/inactive)
   * @param {string} id - User ID
   * @param {Object} data - Status update data
   * @returns {Promise} - API response
   */
  updateUserStatus: async (id, data) => {
    try {
      const response = await api.put(`/admin/users/${id}/status`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update user status' };
    }
  },

  /**
   * Update user role
   * @param {string} id - User ID
   * @param {Object} data - Role update data
   * @returns {Promise} - API response
   */
  updateUserRole: async (id, data) => {
    try {
      const response = await api.put(`/admin/users/${id}/role`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update user role' };
    }
  },

  /**
   * Search users
   * @param {Object} params - Search parameters
   * @returns {Promise} - API response with search results
   */
  searchUsers: async (params) => {
    try {
      const response = await api.get('/admin/users/search', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to search users' };
    }
  },

  /**
   * Get system statistics
   * @returns {Promise} - API response with statistics
   */
  getStatistics: async () => {
    try {
      console.log('[AdminService] Attempting to fetch statistics from /admin/stats');
      try {
        const response = await api.get('/admin/stats');
        console.log('[AdminService] Successfully fetched statistics');
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          // Try alternate path with direct /api prefix
          console.log('[AdminService] 404 error, trying alternate path /api/admin/stats');
          try {
            const response = await api.get('/api/admin/stats');
            console.log('[AdminService] Successfully fetched statistics from alternate path');
            return response.data;
          } catch (alternateError) {
            console.error('[AdminService] Both paths failed:', error, alternateError);
            throw alternateError;
          }
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('[AdminService] Failed to fetch statistics:', error);
      toast.error('Failed to fetch statistics');
      throw error.response?.data || { message: 'Failed to fetch statistics' };
    }
  },

  /**
   * Generate system report
   * @param {Object} params - Report parameters
   * @returns {Promise} - API response with report data
   */
  generateReport: async (params) => {
    try {
      const response = await api.post('/admin/reports/generate', params);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to generate report' };
    }
  },

  /**
   * Get dashboard data
   * @returns {Promise} - API response with dashboard data
   */
  getDashboardData: async () => {
    try {
      console.log('[AdminService] Attempting to fetch dashboard data from /admin/dashboard');
      try {
        const response = await api.get('/admin/dashboard');
        console.log('[AdminService] Successfully fetched dashboard data');
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          // Try alternate path with direct /api prefix
          console.log('[AdminService] 404 error, trying alternate path /api/admin/dashboard');
          try {
            const response = await api.get('/api/admin/dashboard');
            console.log('[AdminService] Successfully fetched dashboard data from alternate path');
            return response.data;
          } catch (alternateError) {
            console.error('[AdminService] Both paths failed:', error, alternateError);
            throw alternateError;
          }
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('[AdminService] Failed to fetch dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
      throw error.response?.data || { message: 'Failed to fetch dashboard data' };
    }
  },

  /**
   * Certificate Management
   */
  getAllCertificates: async (params = {}) => {
    try {
      const response = await api.get('/admin/certificates', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch all certificates' };
    }
  },

  getPendingCertificates: async (params = {}) => {
    try {
      const response = await api.get('/admin/certificates/pending', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch pending certificates' };
    }
  },

  verifyCertificate: async (id, data) => {
    try {
      console.log(`[AdminService] Verifying certificate ${id} with status:`, data.isApproved ? 'approved' : 'rejected');
      try {
        // Try standard endpoint
        const response = await api.put(`/admin/certificates/${id}/verify`, data);
        console.log('[AdminService] Certificate verification successful', response.data);
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          console.log('[AdminService] 404 on primary endpoint, trying fallback');
          // Try alternate endpoint format
          const altResponse = await api.put(`/certificates/${id}/verify`, data);
          console.log('[AdminService] Alternate verification route successful', altResponse.data);
          return altResponse.data;
        }
        throw error;
      }
    } catch (error) {
      console.error('[AdminService] Certificate verification failed:', error);
      throw error.response?.data || { message: 'Failed to verify certificate' };
    }
  },

  /**
   * Listing Management
   */
  getAllListings: async (params = {}) => {
    try {
      console.log('[AdminService] Attempting to fetch listings from /admin/listings');
      try {
        const response = await api.get('/admin/listings', { params });
        console.log('[AdminService] Successfully fetched listings');
        return response.data;
      } catch (apiError) {
        if (apiError.response?.status === 404) {
          // Try alternate path with direct /api prefix
          console.log('[AdminService] 404 error, trying alternate path /api/admin/listings');
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/listings`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
          }
          
          const responseData = await response.json();
          console.log('[AdminService] Successfully fetched listings from alternate path');
          return responseData;
        } else {
          // If it's not a 404 error, just re-throw
          throw apiError;
        }
      }
    } catch (error) {
      console.error('[AdminService] Failed to fetch listings:', error);
      throw error.response?.data || { message: 'Failed to fetch listings' };
    }
  },

  getFlaggedListings: async (params = {}) => {
    try {
      console.log('[AdminService] Attempting to fetch flagged listings from /admin/listings/flagged');
      try {
        const response = await api.get('/admin/listings/flagged', { params });
        console.log('[AdminService] Successfully fetched flagged listings');
        return response.data;
      } catch (apiError) {
        if (apiError.response?.status === 404) {
          // Try alternate path with direct /api prefix
          console.log('[AdminService] 404 error, trying alternate path /api/admin/listings/flagged');
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/listings/flagged`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
          }
          
          const responseData = await response.json();
          console.log('[AdminService] Successfully fetched flagged listings from alternate path');
          return responseData;
        } else {
          // If it's not a 404 error, just re-throw
          throw apiError;
        }
      }
    } catch (error) {
      console.error('[AdminService] Failed to fetch flagged listings:', error);
      throw error.response?.data || { message: 'Failed to fetch flagged listings' };
    }
  },

  updateListingStatus: async (id, data) => {
    try {
      console.log(`[AdminService] Attempting to update listing status for ${id}`);
      try {
        const response = await api.put(`/admin/listings/${id}/status`, data);
        console.log('[AdminService] Successfully updated listing status');
        return response.data;
      } catch (apiError) {
        if (apiError.response?.status === 404) {
          // Try alternate path with direct /api prefix
          console.log('[AdminService] 404 error, trying alternate path');
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/listings/${id}/status`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(data)
          });
          
          if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
          }
          
          const responseData = await response.json();
          console.log('[AdminService] Successfully updated listing status from alternate path');
          return responseData;
        } else {
          // If it's not a 404 error, just re-throw
          throw apiError;
        }
      }
    } catch (error) {
      console.error('[AdminService] Failed to update listing status:', error);
      throw error.response?.data || { message: 'Failed to update listing status' };
    }
  },

  /**
   * Report Management
   */
  getReports: async (params = {}) => {
    try {
      const response = await api.get('/admin/reports', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch reports' };
    }
  },

  updateReportStatus: async (id, data) => {
    try {
      const response = await api.put(`/admin/reports/${id}/status`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update report status' };
    }
  },

  /**
   * Content Management
   */
  getContent: async (type) => {
    try {
      const response = await api.get(`/admin/content/${type}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch content' };
    }
  },

  updateContent: async (type, data) => {
    try {
      const response = await api.put(`/admin/content/${type}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update content' };
    }
  },

  /**
   * Analytics
   */
  getAnalytics: async (params = {}) => {
    try {
      const response = await api.get('/admin/analytics', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch analytics' };
    }
  }
};

export default adminService;
